<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import Button from '@pet/base.button/index.vue';
import { useIntersectionObserver } from '@vueuse/core';
import XImage from '@pet/base.image/index.vue';
import type { CardLogNormalParam, SpikeItem } from './types';
import { getPrice, convertToPercentage, formatSoldNumber } from '../utils/price';

interface Props {
    /**
     * 自定义样式
     */
    customClsName?: string;
    /**
     * 商品id
     */
    itemId: number;
    /**
     * 商品图角标
     */
    itemBanner?: string;
    /**
     * 图片
     */
    itemImage?: string;
    /**
     * 商品名称
     */
    itemName?: string;
    /**
     * 标签
     */
    tags?: string[];
    /**
     * 已售库存
     */
    soldStock?: number;
    /**
     * 商品原价
     */
    itemPrice?: number;
    /**
     * 商品售价
     */
    discountPrice?: number;
    /**
     * 补贴力度
     */
    disCountPercent?: number;
    /**
     *跳转url
     */
    itemOrderUrlKrn?: string;
    /**
     * 品牌名
     */
    chainStoreName?: string;
    /**
     * poi的数量
     */
    itemPoiCount?: number;
    /**
     * poi的名字
     */
    poiName?: string;
    /**
     * 按钮的属性
     */
    marketBtnProps?: any;
    /**
     * 优惠价格-icon
     */
    disCountImg?: string;
    /**
     * 优惠价格
     */
    reducePriceTag?: string;
    /**
     * 品牌名
     */
    brandName?: string;
    /**
     * 价格差值
     */
    priceGap?: number;
    /**
     * 商品详情页链接
     */
    itemDetailUrl?: string;
    btnLog?: any;
}

const PREFIX = 'card';

const taskItemRoot = ref<HTMLDivElement | null>(null);
const targetIsVisible = ref(false);

const props = withDefaults(defineProps<Props>(), {
    itemPoiCount: 0,
    disCountPercent: 0,
    itemPrice: 0,
    discountPrice: 0,
    soldStock: 0,
    itemImage: '',
    itemOrderUrlKrn: '',
    brandName: '',
    priceGap: 0,
});

const { stop } = useIntersectionObserver(taskItemRoot, (arr) => {
    if (arr?.[0]?.isIntersecting) {
        targetIsVisible.value = true;
    }
});

const emit = defineEmits<{
    (e: 'cardClick', params: SpikeItem): void;
    (e: 'btnClick', params: SpikeItem): void;
    (e: 'logEvent', params: CardLogNormalParam): void;
}>();

const cardParam = computed(() => {
    return {
        itemId: props.itemId,
        itemBanner: props.itemBanner!,
        itemImgUrl: props.itemImage!,
        itemTitle: props.itemName!,
        tags: props.tags ?? [],
        soldStock: props.soldStock,
        itemMarketPrice: props.itemPrice,
        discountPrice: props.discountPrice,
        discount: props.disCountPercent,
        itemOrderUrlKrn: props.itemOrderUrlKrn,
        brandName: props.brandName,
        priceGap: props.priceGap,
        itemDetailUrl: props.itemDetailUrl,
    };
});

const onItemClick = (event: Event) => {
    emit('cardClick', cardParam.value);
    emit('logEvent', {
        logType: 'cardClick',
        logParam: cardParam.value,
    });
};

const onButtonClick = (event: Event) => {
    emit('btnClick', cardParam.value);
};

watch(
    () => targetIsVisible.value,
    (visible) => {
        if (visible) {
            emit('logEvent', {
                logType: 'cardShow',
                logParam: cardParam.value,
            });
        }
    },
);

const itemMarketPrice = computed(() => {
    return getPrice(props.itemPrice);
});

const itemDiscountPrice = computed(() => {
    return getPrice(props.discountPrice);
});

const itemPriceGap = computed(() => getPrice(props.priceGap));

const seckillText = computed(() => `已抢${formatSoldNumber(props.soldStock, '万') as string}件`);
</script>

<template>
    <div ref="taskItemRoot" :class="`${PREFIX} ${customClsName}`" @click="onItemClick">
        <div class="card-left">
            <div class="card-left-items">
                <XImage
                    :src="itemImage"
                    class="card-left-items-img"
                    loading="lazy"
                    loading-ctrl="self"
                    root-margin="0px 10% 10% 0px"
                />
            </div>
        </div>
        <div :class="`${PREFIX}-itemsContent`">
            <div :class="`${PREFIX}-itemsContent-itemsContentTop`">
                <div :class="`${PREFIX}-itemsContent-itemsContentTop-itemName`">
                    <img :class="`${PREFIX}-itemsContent-itemsContentTop-itemTag`" src="../assets/bargainTag.png" />
                    <p :class="`${PREFIX}-itemsContent-itemsContentTop-itemName-row-one`">{{ itemName }}</p>
                </div>
                <div :class="`${PREFIX}-itemsContent-storeInfo`">
                    <img :class="`${PREFIX}-itemsContent-storeInfo-shopIcon`" src="../assets/shopIcon.png" alt="" />
                    <p :class="`${PREFIX}-itemsContent-storeInfo-brandText`">{{ brandName }}全国门店通用</p>
                </div>
                <div :class="`${PREFIX}-itemsContent-itemInfo`">
                    <div :class="`${PREFIX}-tags`">
                        <template v-for="(item, index) in tags" :key="index">
                            <div v-if="item !== '爆款'" :class="`${PREFIX}-tags-tagItem`">
                                {{ item }}
                            </div>
                        </template>
                    </div>
                    <div :class="`${PREFIX}-soldStock`">
                        {{ seckillText }}
                    </div>
                </div>
            </div>
            <div :class="`${PREFIX}-itemsContent-buyBtn`" v-log="btnLog ?? {}">
                <Button animation @click="onButtonClick">
                    <div :class="`${PREFIX}-itemsContent-buyBtn-content`">
                        <div :class="`${PREFIX}-itemsContent-buyBtn-content-original-price`">
                            <span :class="`${PREFIX}-itemsContent-buyBtn-content-original-price-desc`">快手价</span>
                            <span :class="`${PREFIX}-itemsContent-buyBtn-content-original-price-unit`">¥</span>
                            <span :class="`${PREFIX}-itemsContent-buyBtn-content-original-price-price`">{{
                                itemMarketPrice
                            }}</span>
                        </div>
                        <div :class="`${PREFIX}-itemsContent-buyBtn-content-discount-price`">
                            <span :class="`${PREFIX}-itemsContent-buyBtn-content-discount-price-desc`">最终价</span>
                            <span :class="`${PREFIX}-itemsContent-buyBtn-content-discount-price-unit`">¥</span>
                            <span :class="`${PREFIX}-itemsContent-buyBtn-content-discount-price-price`">{{
                                itemDiscountPrice
                            }}</span>
                        </div>
                    </div>
                    <div :class="`${PREFIX}-itemsContent-buyBtn-extra`">
                        <div :class="`${PREFIX}-itemsContent-buyBtn-extra-text`">已补</div>
                        <div :class="`${PREFIX}-itemsContent-buyBtn-extra-price`">{{ itemPriceGap || '0' }}元</div>
                    </div>
                </Button>
            </div>
        </div>
    </div>
</template>

<style lang="stylus" scoped>
.card
  display flex
  background-color #fff
  border-radius 16px

  padding 16px
  height 160px
  box-sizing border-box

  &-left
    width 128px
    display flex
    flex-direction column
    justify-content space-between
    flex-shrink 0

    &-items
      position relative
      width 100%
      height 128px

      &-img
        width 100%
        height 100%
        background-size cover
        border-radius 8px

        :deep(.image-main) {
          object-fit cover
        }

      &-banner
        position absolute
        top 0
        left 0
        width 52px
        height 18px
        display flex
        justify-content center
        align-items center
        border-top-left-radius 8px
        border-bottom-right-radius 8px
        color: #FFF;
        font-family PingFang SC
        font-size 10px
        font-weight 500
        line-height normal
        background linear-gradient(45deg, #FE3666 0%, #FF62A8 100%)

    &-seckill
      width 100%


  &-tags
    display flex

    &-tagItem
      font-family PingFang SC
      font-size 10px
      font-weight 400
      line-height 11px
      padding 2px 4px
      border-radius 3px
      border 0.5px solid rgba(9, 0, 29, 0.12)
      margin-right 4px
      color rgba(9, 0, 29, 0.5)

  &-itemsContent
    flex-grow 1
    padding-left 12px
    display flex
    flex-direction column
    font-family PingFang SC
    color #222222
    overflow hidden
    justify-content space-between


    &-storeInfo
      margin 0
      display flex
      align-items center
      justify-content flex-start
      margin-top 8px
      max-width 210px

      &-shopIcon
        width 14px
        height 14px

      &-brandText
        color #666666
        font-size 12px
        font-weight 400
        line-height 16px
        overflow hidden
        text-overflow ellipsis
        white-space nowrap

    &-itemInfo
      display flex
      margin-top 8px
      justify-content space-between
      align-items center
      overflow hidden
      text-overflow ellipsis
      white-space nowrap

    &-buyBtn
      display flex
      flex-direction column
      margin-top 8px
      position relative
      border-radius 5px


      :deep(.btn-size-l) {
        height 50px
        width 210px
      }

      :deep(.btn-main-box) {
        border-radius 0 !important
      }

      :deep(.btn-shine-box) {
        border-radius 0 !important
      }

      :deep(.btn-primary) {
        background url('../assets/btnBkg2.png')
        background-size 100% 100%
      }

      :deep(.btn-primary .btn-words) {
        position absolute
        width 100%
        height 100%
      }

      &-content
        position absolute
        top 0
        left 0
        width 110px
        height 50px
        color: #FE3666;
        font-family PingFang SC
        font-weight 400
        padding 8px
        display flex
        flex-direction column
        align-items flex-start

        &-original-price
          color #787878
          font-size 12px
          font-weight 400
          line-height 12px

          &-unit
            margin-left: 4px;

          &-price
            text-decoration line-through
            margin-left: 1px;


        &-discount-price
          color #FE3666

          &-desc
            font-size 12px
            line-height 12px

          &-unit
            display inline-block
            margin-left 4px
            font-size 15px
            line-height 15px

          &-price
            font-size 20px
            line-height 20px
            font-weight 500
            margin-left 2px

        &-itemInfo
          display flex
          margin-top 8px
          justify-content space-between
          align-items center

      &-discountPrice
        display flex
        align-items center
        font-size 12px
        margin-bottom 3px
        line-height 17px
        color #fe3666
        font-weight 500

        > img
          height 14px
          background-size cover

        > span
          font-weight 500

      &-extra
        position absolute
        top 0
        left 106px
        width 53px
        height 50px
        color #881710
        display flex
        flex-direction column
        align-items center
        justify-content flex-start

        &-text
          font-size 9px
          font-style normal
          font-weight 400
          line-height 10px
          margin-bottom 4px
          margin-top 9px

        &-price
          font-family PingFang SC
          font-size 12px
          font-style normal
          font-weight 600
          line-height 12px

    &-itemsContentTop
      flex-direction column
      justify-content space-between

      &-itemTag
        display block
        width 56px
        height 16px
        margin-right 4px

      &-itemName
        font-size 17px
        font-weight 600
        overflow hidden
        max-width 210px
        line-height 21px
        display flex
        align-items center
        justify-content flex-start

        &-row-one
          max-width 100%
          margin 0
          overflow hidden
          text-overflow ellipsis
          white-space nowrap

  &-soldStock
    color #FE3666
    font-size 11px
    font-style normal
    font-weight 400
    line-height 14px
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
</style>
